import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, RoutesRecognized } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NzMessageService } from 'ng-zorro-antd/message';
import { take } from 'rxjs';
import { DefaultLayout } from './core/layouts/default-layout.model';
import { Layout } from './core/layouts/layout.model';
import { BreadcrumbService } from './core/services/breadcrumb.service';
import { CoreService } from './core/services/core.service';
import { LayoutService } from './core/services/layout.service';
import { MessageService } from './core/services/message.service';
import { UpdateService } from './core/services/update.service';

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
})
export class AppComponent implements OnInit {
  layout: Layout = new DefaultLayout();

  constructor(
    private readonly nzMessageService: NzMessageService,
    private readonly messageService: MessageService,
    public readonly activatedRoute: ActivatedRoute,
    public readonly router: Router,
    private readonly titleService: Title,
    private readonly coreService: CoreService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly updateService: UpdateService
  ) {}

  ngOnInit(): void {
    this.subscribeRoutesRecognized();
    this.subscribeForTitle();
    this.subscribeOnError();
    this.subscribeOnSuccess();
    this.setCss();
    this.getEnvironment();
    this.updateService.subscribeUpdates();
    this.updateService.subscribeUnrecoverableStateHandler();
  }

  private setCss() {
    this.coreService
      .getCss$()
      .pipe(take(1))
      .subscribe({
        next: (cssPage) => {
          const style = document.createElement('style');
          style.innerHTML = cssPage;
          document.head.appendChild(style);
        },
        error: console.error,
      });
  }

  private getEnvironment() {
    this.coreService.fetchEnvironment();
  }

  private subscribeRoutesRecognized(): void {
    this.router.events.pipe(untilDestroyed(this)).subscribe((data) => {
      if (data instanceof RoutesRecognized) {
        const deepestLayout = this.findDeepestLayout(data.state.root);
        this.layout = LayoutService.getLayout(deepestLayout?.data?.layout);
      }
    });
  }

  private findDeepestLayout(route: ActivatedRouteSnapshot | null, depth = 0): ActivatedRouteSnapshot | null {
    if (!route || depth > 10) return null;

    for (let i = (route.children ?? []).length - 1; i >= 0; i--) {
      const childResult = this.findDeepestLayout(route.children[i], depth + 1);
      if (childResult?.data?.layout) {
        return childResult;
      }
    }
    return route.data?.layout ? route : null;
  }

  private subscribeOnError(): void {
    // no need to unsub because we're in app.component
    this.messageService
      .onError$()
      .pipe(untilDestroyed(this))
      .subscribe((error: string) => {
        this.nzMessageService.error(error);
      });
  }

  private subscribeOnSuccess(): void {
    this.messageService
      .onSuccess$()
      .pipe(untilDestroyed(this))
      .subscribe((success: string) => {
        this.nzMessageService.success(success);
      });
  }

  private subscribeForTitle(): void {
    const appTitle = this.titleService.getTitle();
    this.breadcrumbService
      .getBreadcrumbs$()
      .pipe(untilDestroyed(this))
      .subscribe((breadcrumbs) => {
        const title = [appTitle]
          .concat(breadcrumbs.map((breadcrumb) => breadcrumb.label))
          .reverse()
          .join(' / ');
        this.titleService.setTitle(title);
      });
  }
}
